import { useMemo } from "react";
import { useExperimental } from "../../hooks/useAdminData";
import ExperimentalChart from "./ExperimentalChart";
import UtilizationLegend from "./UtilizationLegend";
import type { ExperimentalWeekPoint } from "../../types/dashboard";
import DashboardQueryStatus from "./DashboardQueryStatus";

const WINDOW_WEEKS = 4;

interface TimekeeperLineViewProps {
  selectedWeekStart: string | null;
}

/** Returns YYYY-MM-DD for d shifted by `days` days. */
function shiftDate(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtWeekLabel(startIso: string, endIso: string): string {
  const s = new Date(startIso + "T00:00:00");
  const e = new Date(endIso + "T00:00:00");
  const fmt = (d: Date) =>
    `${d.getMonth() + 1}/${String(d.getDate()).padStart(2, "0")}`;
  return `${fmt(s)}-${fmt(e)}`;
}

export default function TimekeeperLineView({
  selectedWeekStart,
}: TimekeeperLineViewProps) {
  const { data, loading, error, refetch, isFetching, isPlaceholderData } =
    useExperimental(selectedWeekStart);

  /** Ordered array of the 4 week-starts (Mon, YYYY-MM-DD) ending at selectedWeekStart. */
  const windowWeeks = useMemo(() => {
    if (!selectedWeekStart) return [];
    return Array.from({ length: WINDOW_WEEKS }, (_, i) =>
      shiftDate(selectedWeekStart, -7 * (WINDOW_WEEKS - 1 - i)),
    );
  }, [selectedWeekStart]);

  const windowLabel = useMemo(() => {
    if (windowWeeks.length === 0) return "";
    const start = windowWeeks[0];
    const end = shiftDate(windowWeeks[windowWeeks.length - 1], 6);
    return fmtWeekLabel(start, end);
  }, [windowWeeks]);

  const status = (
    <DashboardQueryStatus
      label="trend data"
      loading={loading}
      updating={isFetching && !loading}
      error={error}
      onRetry={() => void refetch()}
    />
  );
  if (!data) return status;
  if (data.professionals.length === 0) {
    return (
      <>
        {status}
        <p className="text-center text-muted-foreground text-sm py-12">
          No trend data available for licensed professionals.
        </p>
      </>
    );
  }

  return (
    <div className="space-y-8 pb-6">
      {status}
      <UtilizationLegend />
      {data.professionals.map((prof) => {
        const byWeek = new Map<string, ExperimentalWeekPoint>();
        for (const w of prof.weekly_hours) byWeek.set(w.week_start, w);

        const filled: ExperimentalWeekPoint[] = windowWeeks.map((ws) => {
          const hit = byWeek.get(ws);
          if (hit) return hit;
          const we = shiftDate(ws, 6);
          return {
            week_start: ws,
            week_end: we,
            week_label: fmtWeekLabel(ws, we),
            hours: 0,
            hg_hours: 0,
            utilization_pct: 0,
          };
        });

        const profSlice = {
          ...prof,
          weekly_hours: isPlaceholderData ? prof.weekly_hours : filled,
        };

        return (
          <div
            key={prof.prof_id}
            className={`bg-card border border-border rounded-lg p-6${isPlaceholderData ? " opacity-60" : ""}`}
          >
            <h3 className="text-base font-semibold text-foreground">
              {prof.prof_name}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5 mb-4">
              {prof.pre_hg_period_label}: {prof.pre_hg_avg.toFixed(1)}h/week ±{" "}
              {(prof.pre_hg_std ?? 0).toFixed(1)}h
            </p>
            {windowWeeks.length === 0 ? (
              <p className="text-center text-muted-foreground text-sm py-4">
                No data available for {windowLabel}
              </p>
            ) : (
              <ExperimentalChart data={profSlice} />
            )}
          </div>
        );
      })}
    </div>
  );
}
